import { useEffect, useState } from 'react';
import usePreviousRef from 'hooks/usePreviousRef';
import { DrawableAudioPlayer } from 'utils/audio/types';
import { PlaybackState } from '../../types';

interface UsePlaybackStateConfig {
  loop: boolean | undefined;
  player: DrawableAudioPlayer | undefined;
}

interface UsePlaybackStateResult {
  playbackState: PlaybackState;
}

function getPlaybackState(
  player: DrawableAudioPlayer | undefined,
): PlaybackState {
  const playing = !(player?.paused ?? true);
  return playing ? 'playing' : 'paused';
}

export default function usePlaybackState({
  loop,
  player,
}: UsePlaybackStateConfig): UsePlaybackStateResult {
  const [playbackState, setPlaybackState] = useState<PlaybackState>(
    getPlaybackState(player),
  );
  const loopRef = usePreviousRef(loop);

  // register callbacks to keep playback state in-sync with player
  useEffect(() => {
    if (!player) return;

    const handlePlay = () => setPlaybackState('playing');
    const handlePause = () => setPlaybackState('paused');
    const handleEnded = () => {
      // if the audio is to be looped, then we don't want to set the playback
      // state to "ended"
      if (!loopRef.current) {
        setPlaybackState('ended');
      }
    };

    setPlaybackState(getPlaybackState(player));

    player.on('play', handlePlay);
    player.on('pause', handlePause);
    player.on('ended', handleEnded);

    return () => {
      player.off('play', handlePlay);
      player.off('pause', handlePause);
      player.off('ended', handleEnded);
    };
  }, [loopRef, player]);

  return { playbackState };
}
